import React, { useEffect, useState, useMemo } from "react";
import TodayReport from "./report/todayReport";
import MonthalyReport from "./report/monthalyReport";
import Spinner from "../loading/spinner";
import StatsCard from "./patientCount";
import IncomeStats from "./incomeStats";
import CustomReport from "./customReport";
import { toggleState } from "../../specialMethod/toggleState";

// ============================================
// LOCALSTORAGE HELPERS
// ============================================

const getAppointments = () => {
  try {
    return JSON.parse(localStorage.getItem("appointments")) || [];
  } catch {
    return [];
  }
};

const getClinics = () => {
  try {
    return JSON.parse(localStorage.getItem("clinics")) || [];
  } catch {
    return [];
  }
};

// ============================================
// COMPUTATION FUNCTIONS
// ============================================

// Compute income stats for today (or any date)
const computeIncomeStats = (appointments, date = null) => {
  let filtered = appointments;
  if (date) {
    const dateStr = date.toISOString().split("T")[0];
    filtered = appointments.filter(apt => apt.appointmentDate === dateStr);
  }
  // Consider only paid and visited appointments
  const paid = filtered.filter(apt => apt.paymentStatus === "PAID" && apt.appointmentvisitStatus === "VISITED");
  const totalAmount = paid.reduce((sum, apt) => sum + (apt.paymentAmount || 0), 0);
  const upiAmount = paid.filter(apt => apt.paymentMode === "upi").reduce((sum, apt) => sum + (apt.paymentAmount || 0), 0);
  const cashAmount = paid.filter(apt => apt.paymentMode === "cash").reduce((sum, apt) => sum + (apt.paymentAmount || 0), 0);
  return { totalAmount, upiAmount, cashAmount };
};

// Compute visits count
const computeVisits = (appointments, days = 0) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - days);
  const startStr = startDate.toISOString().split("T")[0];
  const todayStr = today.toISOString().split("T")[0];
  const filtered = appointments.filter(apt => {
    const aptDate = apt.appointmentDate;
    return aptDate >= startStr && aptDate <= todayStr && apt.appointmentvisitStatus === "VISITED";
  });
  return filtered.length;
};

// Generate report data for graph (aggregated by day for last N days)
const generateReportData = (appointments, days = 7, type = "income") => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const result = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split("T")[0];
    const dayAppointments = appointments.filter(apt => apt.appointmentDate === dateStr);
    let value = 0;
    if (type === "income") {
      value = dayAppointments
        .filter(apt => apt.paymentStatus === "PAID" && apt.appointmentvisitStatus === "VISITED")
        .reduce((sum, apt) => sum + (apt.paymentAmount || 0), 0);
    } else {
      value = dayAppointments.filter(apt => apt.appointmentvisitStatus === "VISITED").length;
    }
    result.push({ date: dateStr, value });
  }
  return result;
};

// Generate yearly report (group by month)
const generateYearlyReport = (appointments, type = "income") => {
  const today = new Date();
  const year = today.getFullYear();
  const result = [];
  for (let month = 0; month < 12; month++) {
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    const startStr = startDate.toISOString().split("T")[0];
    const endStr = endDate.toISOString().split("T")[0];
    const monthAppointments = appointments.filter(apt => apt.appointmentDate >= startStr && apt.appointmentDate <= endStr);
    let value = 0;
    if (type === "income") {
      value = monthAppointments
        .filter(apt => apt.paymentStatus === "PAID" && apt.appointmentvisitStatus === "VISITED")
        .reduce((sum, apt) => sum + (apt.paymentAmount || 0), 0);
    } else {
      value = monthAppointments.filter(apt => apt.appointmentvisitStatus === "VISITED").length;
    }
    result.push({ month: startDate.toLocaleString('default', { month: 'short' }), value });
  }
  return result;
};

// ============================================
// MAIN COMPONENT
// ============================================

const PatientTally = () => {
  const [state, setState] = useState({
    loadinSpinner: false,
    tabType: "income",
    dayTabType: "day7Report",
    result: [],
    customReport: false,
    clinic: []
  });

  // Load all data from localStorage
  const allAppointments = useMemo(() => getAppointments(), []);
  const allClinics = useMemo(() => getClinics(), []);

  // Compute stats
  const todayVisits = computeVisits(allAppointments, 0);
  const monthVisits = computeVisits(allAppointments, 30);
  const todayIncome = computeIncomeStats(allAppointments, new Date());
  const monthIncome = computeIncomeStats(allAppointments, null, 30); // we need to compute last 30 days income
  // For month income: we can compute from appointments in last 30 days
  const computeMonthIncome = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - 30);
    const startStr = startDate.toISOString().split("T")[0];
    const todayStr = today.toISOString().split("T")[0];
    const filtered = allAppointments.filter(apt => apt.appointmentDate >= startStr && apt.appointmentDate <= todayStr);
    return computeIncomeStats(filtered);
  };
  const monthIncomeStats = computeMonthIncome();

  // Generate report data based on tabType and dayTabType
  useEffect(() => {
    let report = [];
    if (state.dayTabType === "day7Report") {
      report = generateReportData(allAppointments, 7, state.tabType);
    } else {
      report = generateYearlyReport(allAppointments, state.tabType);
    }
    toggleState("result", report, setState);
  }, [state.tabType, state.dayTabType, allAppointments]);

  // Set clinic list
  useEffect(() => {
    if (allClinics.length > 0) {
      toggleState("clinic", allClinics, setState);
    }
  }, [allClinics]);

  // Loading: always false since we sync
  useEffect(() => {
    toggleState("loadinSpinner", false, setState);
  }, []);

  return (
    <>
      <section className="text-gray-600 body-font">
        <div className="container max-w-6xl px-5 py-10 mx-auto">
          <div className="flex flex-wrap -mx-2">
            <div className="px-2 pl-10 w-full md:w-1/4">
              <StatsCard title="Today's Visits" value={todayVisits} />
            </div>
            <div className="pl-10 px-2 w-full md:w-3/4">
              <IncomeStats incomeData={todayIncome} />
            </div>
          </div>
        </div>
      </section>
      <section className="text-gray-600 body-font">
        <div className="container max-w-6xl px-5 pb-10 mx-auto">
          <div className="flex flex-wrap -mx-2">
            <div className="px-2 pl-10 w-full md:w-1/4">
              <StatsCard title="Past 30 Day's Visits" value={monthVisits} />
            </div>
            <div className="pl-10 px-2 w-full md:w-3/4">
              <IncomeStats incomeData={monthIncomeStats} />
            </div>
          </div>
        </div>
      </section>
      <div className="flex flex-wrap max-w-screen-xl mx-auto">
        <div className="flex flex-col border rounded-lg w-screen h-1/2 px-10 py-8 text-gray-700">
          {/* Toggle Buttons */}
          <div className="flex justify-between items-center mb-4 w-full">
            <div className="flex justify-center w-full space-x-4">
              <button
                type="button"
                onClick={() => toggleState("dayTabType", "day7Report", setState)}
                className={`py-2.5 md:pl-60 pl-0 px-5 mb-2 ${state.dayTabType === "day7Report" ? "underline text-red-600" : "text-red-800"} text-sm font-medium focus:outline-none focus:z-10`}
              >
                7 day
              </button>
              <button
                type="button"
                onClick={() => toggleState("dayTabType", "YearReport", setState)}
                className={`py-2.5 px-5 pr-5 mb-2 ${state.dayTabType === "YearReport" ? "underline text-red-800" : "text-red-600"} text-sm font-medium`}
              >
                1 Year
              </button>
            </div>
            <div className="flex md:ml-auto items-center flex-shrink-0 space-x-4">
              <button
                type="button"
                onClick={() => toggleState("tabType", "income", setState)}
                className={`py-2 px-2 mb-2 text-sm font-medium rounded-lg ${state.tabType === "income" ? "text-white border-red-700 bg-red-700" : "text-black bg-gray-200 border-gray-400"} focus:outline-none border focus:z-10`}
              >
                Total Income
              </button>
              <button
                type="button"
                onClick={() => toggleState("tabType", "visit", setState)}
                className={`py-2 px-2 mb-2 text-sm font-medium rounded-lg ${state.tabType === "visit" ? "text-white border-red-700 bg-red-700" : "text-black bg-gray-200 border-gray-400"} focus:outline-none border focus:z-10`}
              >
                Patient Visits
              </button>
            </div>
          </div>

          {/* Conditional rendering for reports */}
          {state.tabType === "income" ? (
            <TodayReport result={state.result} />
          ) : (
            <MonthalyReport result={state.result} />
          )}

          {/* Custom Report Button */}
          <div className="flex justify-end mt-4">
            <p
              className="py-2.5 px-5 cursor-pointer mb-10 rounded-md text-sm font-medium text-white bg-red-800 focus:outline-none focus:z-10"
              onClick={() => toggleState("customReport", true, setState)}
            >
              Custom Report
            </p>
          </div>
        </div>
      </div>
      {state.loadinSpinner && <Spinner />}
      {state.customReport && <CustomReport reset={() => toggleState("customReport", false, setState)} />}
    </>
  );
};

export default PatientTally;